/**
 * LabelPrinter.jsx - Botão de Impressão de Etiquetas
 * CG Business ERP - P7
 * 
 * Componente reutilizável para gerar PDFs de etiquetas com código de barras.
 */

import { useState } from 'react';
import { Printer, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { getStoredAuthToken } from '@/lib/authStorage';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Label size options
const LABEL_SIZES = [
  { value: 'small', label: 'Pequena (50x25mm)', description: '40 por A4' },
  { value: 'medium', label: 'Média (70x35mm)', description: '24 por A4' },
  { value: 'large', label: 'Grande (100x50mm)', description: '10 por A4' },
];

/**
 * LabelPrinter - Button to generate barcode label PDFs
 * 
 * @param {Object} props
 * @param {string} props.type - 'product' | 'item' | 'batch' | 'location'
 * @param {string} props.productId - Product ID (for type='product')
 * @param {string} props.itemId - Item ID (for type='item')
 * @param {string[]} props.itemIds - Array of item IDs (for type='batch')
 * @param {string} props.locationId - Location ID (for type='location')
 * @param {string} props.variant - Button variant
 * @param {string} props.size - Button size
 */
export function LabelPrinter({
  type = 'item',
  productId,
  itemId,
  itemIds = [],
  locationId,
  variant = 'outline',
  size = 'sm',
  showLabel = true,
  disabled = false
}) {
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [options, setOptions] = useState({
    size: 'medium',
    quantity: 1,
    showPrice: true,
    showLocation: true
  });
  
  const getToken = () => getStoredAuthToken();
  
  const generateLabels = async () => {
    setLoading(true);
    
    try {
      let url = '';
      let fetchOptions = {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      };
      
      const params = new URLSearchParams();
      params.append('size', options.size);
      params.append('show_price', options.showPrice);
      params.append('show_location', options.showLocation);
      
      switch (type) {
        case 'product':
          if (!productId) throw new Error('Product ID required');
          params.append('quantity', options.quantity);
          url = `${API_URL}/barcode/labels/product/${productId}?${params}`;
          break;
          
        case 'item':
          if (!itemId) throw new Error('Item ID required');
          url = `${API_URL}/barcode/labels/item/${itemId}?${params}`;
          break;
          
        case 'batch':
          if (!itemIds.length) throw new Error('Item IDs required');
          url = `${API_URL}/barcode/labels/items/batch?${params}`;
          fetchOptions.method = 'POST';
          fetchOptions.headers['Content-Type'] = 'application/json';
          fetchOptions.body = JSON.stringify(itemIds);
          break;
          
        case 'location':
          if (!locationId) throw new Error('Location ID required');
          params.append('limit', 100);
          url = `${API_URL}/barcode/labels/location/${locationId}?${params}`;
          break;
          
        default:
          throw new Error('Invalid label type');
      }
      
      const response = await fetch(url, fetchOptions);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao gerar etiquetas');
      }
      
      // Download PDF
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Extract filename from Content-Disposition or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'etiquetas.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Etiquetas geradas!', {
        description: `PDF pronto para impressão`
      });
      
      setDialogOpen(false);
      
    } catch (error) {
      console.error('[LabelPrinter] Error:', error);
      toast.error(error.message || 'Erro ao gerar etiquetas');
    } finally {
      setLoading(false);
    }
  };
  
  // Quick print for single item
  const handleQuickPrint = () => {
    if (type === 'item' || type === 'product') {
      generateLabels();
    } else {
      setDialogOpen(true);
    }
  };
  
  return (
    <>
      {/* Simple button for single items */}
      {(type === 'item' || (type === 'product' && options.quantity === 1)) ? (
        <Button
          variant={variant}
          size={size}
          onClick={handleQuickPrint}
          disabled={disabled || loading}
          data-testid="print-label-button"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Printer className="h-4 w-4" />
          )}
          {showLabel && <span className="ml-2">Etiqueta</span>}
        </Button>
      ) : (
        /* Dropdown for batch/product with options */
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={variant}
              size={size}
              disabled={disabled || loading}
              data-testid="print-labels-dropdown"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Printer className="h-4 w-4" />
              )}
              {showLabel && <span className="ml-2">Etiquetas</span>}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Imprimir Etiquetas</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setDialogOpen(true)}>
              <Download className="h-4 w-4 mr-2" />
              Gerar PDF com opções
            </DropdownMenuItem>
            <DropdownMenuItem onClick={generateLabels}>
              <Printer className="h-4 w-4 mr-2" />
              Impressão rápida (tamanho médio)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      
      {/* Options Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Opções de Etiqueta
            </DialogTitle>
            <DialogDescription>
              Configure o formato das etiquetas antes de gerar o PDF
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Size */}
            <div className="space-y-2">
              <Label>Tamanho da Etiqueta</Label>
              <Select
                value={options.size}
                onValueChange={(v) => setOptions(o => ({ ...o, size: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LABEL_SIZES.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <div>
                        <span>{s.label}</span>
                        <span className="text-xs text-muted-foreground ml-2">({s.description})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Quantity (for products) */}
            {type === 'product' && (
              <div className="space-y-2">
                <Label>Quantidade de Etiquetas</Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={options.quantity}
                  onChange={(e) => setOptions(o => ({ ...o, quantity: parseInt(e.target.value) || 1 }))}
                />
              </div>
            )}
            
            {/* Show price */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showPrice"
                checked={options.showPrice}
                onCheckedChange={(v) => setOptions(o => ({ ...o, showPrice: v }))}
              />
              <label htmlFor="showPrice" className="text-sm font-medium cursor-pointer">
                Mostrar preço na etiqueta
              </label>
            </div>
            
            {/* Show location */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showLocation"
                checked={options.showLocation}
                onCheckedChange={(v) => setOptions(o => ({ ...o, showLocation: v }))}
              />
              <label htmlFor="showLocation" className="text-sm font-medium cursor-pointer">
                Mostrar localização na etiqueta
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={generateLabels} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A gerar...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Gerar PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default LabelPrinter;
